import React, { useState } from 'react';

interface AdasAttribute {
  adas_attribute_name: string;
  adas_attribute_value: string;
}

interface AdasSensor {
  adas_sensor_type: string;
  adas_sensor_location: string;
  adas_sensor_count: string;
}

interface AdasSystem {
  adas_system_id: string;
  adas_system_name: string;
  adas_system_oem_name: string;
  adas_system_oem_group_name?: string;
  availability?: string;
  installed?: string;
  adas_attributes?: AdasAttribute[];
  adas_sensors?: AdasSensor[];
}

interface AdasSectionProps {
  adasSystems?: AdasSystem[];
}

const sensorIcon = (type: string) => {
  const t = type.toLowerCase();
  if (t.includes('camera')) return '📷';
  if (t.includes('radar')) return '📡';
  if (t.includes('ultrasonic')) return '🔊';
  if (t.includes('lidar')) return '🔦';
  return '🧩';
};

const AdasSection: React.FC<AdasSectionProps> = ({ adasSystems = [] }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (adasSystems.length === 0) return null;

  return (
    <div className="border rounded p-4 shadow-md bg-white">
      <h3 className="text-lg font-semibold mb-4">Advanced Driver Assistance Systems (ADAS)</h3>
      <div className="space-y-4">
        {adasSystems.map((system, index) => {
          const isOpen = openIndex === index;

          return (
            <div key={index} className="border rounded p-3">
              <button
                className="w-full text-left flex justify-between items-center"
                onClick={() => setOpenIndex(isOpen ? null : index)}
              >
                <span className="font-semibold text-blue-800">
                  {system.adas_system_name}: {system.adas_system_oem_name}
                </span>
                <span className="text-sm text-gray-500">{isOpen ? '▲' : '▼'}</span>
              </button>

              {isOpen && (
                <div className="mt-3 space-y-2 text-sm text-gray-800">
                  <div>
                    <span className="text-gray-600">OEM Group:</span> {system.adas_system_oem_group_name || 'N/A'} | 
                    <span className="ml-1">Availability:</span> {system.availability || 'N/A'}
                    {system.installed === 'Y' && (
                      <span className="ml-2 text-green-600 font-semibold">✅ Installed</span>
                    )}
                  </div>

                  {system.adas_attributes?.length > 0 && (
                    <div>
                      <div className="font-medium mt-2">Attributes:</div>
                      <ul className="ml-5 list-disc">
                        {system.adas_attributes.map((attr, i) => (
                          <li key={i}>
                            {attr.adas_attribute_name}: {attr.adas_attribute_value}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {system.adas_sensors?.length > 0 && (
                    <div>
                      <div className="font-medium mt-2">Sensors:</div>
                      <ul className="ml-5 list-disc">
                        {system.adas_sensors.map((sensor, j) => {
                          const count = parseInt(sensor.adas_sensor_count || '1', 10);
                          return Array.from({ length: count }).map((_, k) => (
                            <li key={`${j}-${k}`}>
                              {sensorIcon(sensor.adas_sensor_type)} {sensor.adas_sensor_type} at {sensor.adas_sensor_location} (#{k + 1})
                            </li>
                          ));
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdasSection;
